import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import { mergeSalaryFiles } from "../../api";
import { useAlert } from "../../context/AlertContext";

// Same tokens as PayrollProcess so the block reads as part of Step 3.
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
const MONEY_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px', bgcolor: '#16a34a',
  '&:hover': { bgcolor: '#15803d' },
};

// The payroll run produces one salary file per team head. Keys match the backend
// upload fields (file_ho, file_vinod, …) in candidates/salary_file_merge_view.py.
const TEAM_SLOTS = [
  { key: 'ho', label: 'HO', tile: 'HO', color: '#8a6a5c' },
  { key: 'vinod', label: 'Vinod', tile: 'V', color: '#2f6bff' },
  { key: 'sathish', label: 'Sathish', tile: 'S', color: '#0f8f83' },
  { key: 'mdh', label: 'MDH', tile: 'M', color: '#7b4fd6' },
];
const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv'];
const EMPTY_FILES = { ho: null, vinod: null, sathish: null, mdh: null };

const inr = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => (n === null || n === undefined ? '—' : `₹${inr.format(Number(n) || 0)}`);
const sizeText = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const extensionError = (file) => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext)
    ? null
    : `"${file.name}" is a .${ext} file. Choose the .xlsx, .xls or .csv salary file.`;
};

const saveBase64File = (base64, filename) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const url = window.URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Payslip Finalization → upload the four team salary files, merge them into one
 * consolidated workbook (Bank Details + Salary Details + Merge Summary) and download it.
 * All validation and merging happens on the server; this component only collects the
 * files, shows progress, and surfaces exactly what to fix.
 */
const SalaryFileMerge = ({ month, year }) => {
  const { showSuccess } = useAlert();
  const [files, setFiles] = useState(EMPTY_FILES);
  const [slotErrors, setSlotErrors] = useState({});
  const [phase, setPhase] = useState('idle'); // idle | uploading | merging
  const [uploadPct, setUploadPct] = useState(0);
  const [failure, setFailure] = useState(null); // { error, problems[] }
  const [result, setResult] = useState(null);   // { filename, file_base64, summary }

  const busy = phase !== 'idle';

  const resetOutcome = () => {
    setFailure(null);
    setResult(null);
  };

  const handleChoose = (slot) => (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    resetOutcome();
    setFiles((prev) => ({ ...prev, [slot]: file }));
    setSlotErrors((prev) => ({ ...prev, [slot]: extensionError(file) }));
  };

  const handleRemove = (slot) => {
    resetOutcome();
    setFiles((prev) => ({ ...prev, [slot]: null }));
    setSlotErrors((prev) => ({ ...prev, [slot]: null }));
  };

  const handleMerge = async () => {
    resetOutcome();
    const missing = TEAM_SLOTS.filter((s) => !files[s.key]).map((s) => s.label);
    if (missing.length) {
      setFailure({ error: `Upload all four salary files before merging. Missing: ${missing.join(', ')}.`, problems: [] });
      return;
    }
    const invalid = TEAM_SLOTS.filter((s) => slotErrors[s.key]).map((s) => s.label);
    if (invalid.length) {
      setFailure({
        error: `${invalid.join(', ')} ${invalid.length === 1 ? 'file is' : 'files are'} not a valid salary file. Replace ${invalid.length === 1 ? 'it' : 'them'} and merge again.`,
        problems: [],
      });
      return;
    }

    setPhase('uploading');
    setUploadPct(0);
    try {
      const data = await mergeSalaryFiles({
        files,
        month,
        year,
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadPct(pct);
          if (pct >= 100) setPhase('merging');
        },
      });
      setResult(data);
      setSlotErrors({});
      showSuccess('Salary files merged. Download the consolidated file below.');
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.file_errors && Object.keys(body.file_errors).length) {
        setSlotErrors((prev) => ({ ...prev, ...body.file_errors }));
      }
      setFailure({
        error: body.error || 'The files could not be merged. Check your connection and try again.',
        problems: body.problems || [],
      });
    } finally {
      setPhase('idle');
    }
  };

  const summary = result?.summary;
  const totals = summary?.totals;
  const recon = summary?.reconciliation;

  return (
    <Box sx={{ mb: 3, pb: 3, borderBottom: '1px dashed #e5e7eb' }}>
      <Typography sx={{ fontWeight: 800, color: '#111827', fontSize: '0.98rem' }}>
        Merge team salary files
      </Typography>
      <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, mb: 2 }}>
        Upload the four salary files from this payroll run, then <b>Merge Files</b> to download one consolidated file with Bank Details and Salary Details. You can upload that file below to <b>Generate Payslip</b>.
        {month && year ? <> Files must be for <b>{month} {year}</b>.</> : null}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
        {TEAM_SLOTS.map((slot) => {
          const file = files[slot.key];
          const error = slotErrors[slot.key];
          const inputId = `merge-salary-file-${slot.key}`;
          return (
            <Box
              key={slot.key}
              sx={{
                display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0, p: 1.75, borderRadius: '14px',
                bgcolor: '#fff', border: `1px solid ${error ? '#fca5a5' : file ? '#dcfce7' : '#eef0f4'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.78rem', color: slot.color, bgcolor: `${slot.color}1a` }}>
                  {slot.tile}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>{slot.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>{slot.label} salary file</Typography>
                </Box>
                {file && !error && <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />}
              </Box>

              <input id={inputId} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleChoose(slot.key)} />

              {file ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, px: 1.25, py: 1, borderRadius: '10px', bgcolor: '#f8fafc' }}>
                  <InsertDriveFileOutlinedIcon sx={{ color: '#6b7280', fontSize: 18, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography title={file.name} sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>{sizeText(file.size)}</Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: '#9ca3af', px: 1.25, py: 1, borderRadius: '10px', border: '1.5px dashed #e5e7eb', textAlign: 'center' }}>
                  No file chosen
                </Typography>
              )}

              {error && (
                <Typography variant="caption" sx={{ color: '#dc2626', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{error}</Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                <label htmlFor={inputId} style={{ flex: 1 }}>
                  <Button component="span" variant="outlined" size="small" fullWidth disabled={busy} startIcon={<CloudUploadIcon />} sx={{ ...OUTLINED_BTN, height: 36 }}>
                    {file ? 'Replace' : 'Choose file'}
                  </Button>
                </label>
                {file && (
                  <Button size="small" disabled={busy} onClick={() => handleRemove(slot.key)} sx={{ textTransform: 'none', fontWeight: 700, color: '#9ca3af', borderRadius: '12px' }}>
                    Remove
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
        <Button
          variant="contained"
          disableElevation
          startIcon={<MergeTypeIcon />}
          onClick={handleMerge}
          disabled={busy}
          sx={{ ...GRADIENT_BTN, height: '48px', minWidth: '200px' }}
        >
          Merge Files
        </Button>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          {TEAM_SLOTS.filter((s) => files[s.key]).length} of 4 files added
        </Typography>
      </Box>

      {busy && (
        <Box sx={{ mt: 2, maxWidth: 520 }} role="status" aria-live="polite">
          <LinearProgress
            variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
            value={uploadPct}
            sx={{ height: 8, borderRadius: 99, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { borderRadius: 99 } }}
          />
          <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.75, display: 'block' }}>
            {phase === 'uploading' ? `Uploading files… ${uploadPct}%` : 'Checking and merging files…'}
          </Typography>
        </Box>
      )}

      {failure && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: '14px' }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{failure.error}</AlertTitle>
          {failure.problems.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
              {failure.problems.map((p) => <li key={p}>{p}</li>)}
            </Box>
          )}
        </Alert>
      )}

      {result && summary && (
        <Box sx={{ mt: 2, p: { xs: 1.75, sm: 2.25 }, borderRadius: '16px', border: '1px solid #dcfce7', bgcolor: '#f7fdf9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#16a34a' }} />
            <Typography sx={{ fontWeight: 800, color: '#111827' }}>
              Merged {totals.salary_rows || totals.bank_rows} employees{summary.month ? ` · ${summary.month}` : ''}
            </Typography>
            {recon && (
              <Chip
                size="small"
                label={recon.matches ? 'Bank amount matches Net Pay' : `Bank and Net Pay differ by ${money(Math.abs(recon.difference))}`}
                sx={{ fontWeight: 700, bgcolor: recon.matches ? '#dcfce7' : '#fef3c7', color: recon.matches ? '#15803d' : '#92400e' }}
              />
            )}
          </Box>

          <TableContainer sx={{ borderRadius: '12px', border: '1px solid #eef0f4', bgcolor: '#fff', mb: 1.5 }}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                  <TableCell>Team</TableCell>
                  <TableCell align="right">Salary rows</TableCell>
                  <TableCell align="right">Net Pay</TableCell>
                  <TableCell align="right">Bank rows</TableCell>
                  <TableCell align="right">Bank Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ '& td': { fontVariantNumeric: 'tabular-nums' } }}>
                {summary.teams.map((t) => (
                  <TableRow key={t.team}>
                    <TableCell sx={{ fontWeight: 600 }}>{t.team}</TableCell>
                    <TableCell align="right">{t.salary_rows}</TableCell>
                    <TableCell align="right">{money(t.net_pay)}</TableCell>
                    <TableCell align="right">{t.bank_rows}</TableCell>
                    <TableCell align="right">{money(t.bank_amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 800, bgcolor: '#f8fafc' } }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">{totals.salary_rows}</TableCell>
                  <TableCell align="right">{money(totals.net_pay)}</TableCell>
                  <TableCell align="right">{totals.bank_rows}</TableCell>
                  <TableCell align="right">{money(totals.bank_amount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {summary.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '12px' }}>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {summary.warnings.map((w) => <li key={w}>{w}</li>)}
              </Box>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              disableElevation
              startIcon={<DownloadIcon />}
              onClick={() => saveBase64File(result.file_base64, result.filename)}
              sx={{ ...MONEY_BTN, height: '48px', minWidth: '220px' }}
            >
              Download Merged File
            </Button>
            <Typography variant="body2" sx={{ color: '#6b7280', overflowWrap: 'anywhere' }}>
              {result.filename} · {summary.sheets.join(', ')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SalaryFileMerge;
